// MapService implementation using Yandex Maps JS API 3.0 (ymaps3)
import { MAP_COLORS, } from "../types/map";
import { routeCalculationService } from "@/services/RouteCalculationService";
import { loadYmaps3 } from "@/utils/ymaps3Loader";
export class MapService {
    constructor() {
        this.api = null;
        this.map = null;
        this.locators = new Map();
        this.routes = new Map();
        this.isInitialized = false;
        this.container = null;
    }
    // ─── Coordinate conversion ───────────────────────────────────────
    // Leaflet used [lat, lng]; ymaps3 uses [lng, lat] (GeoJSON order).
    // Conversion happens ONLY inside MapService.
    toLngLat(point) {
        return [point.longitude, point.latitude];
    }
    // ─── Initialization ──────────────────────────────────────────────
    async initializeMap(container, config) {
        this.ensureDestroyed();
        this.container = container;
        this.api = await loadYmaps3();
        this.map = new this.api.YMap(container, {
            location: {
                center: this.toLngLat(config.center),
                zoom: config.zoom,
            },
            zoomRange: {
                min: config.minZoom ?? 2,
                max: config.maxZoom ?? 19,
            },
        });
        // Base scheme layer (Yandex tiles)
        this.map.addChild(new this.api.YMapDefaultSchemeLayer({}));
        // Features layer for markers & polylines
        this.map.addChild(new this.api.YMapDefaultFeaturesLayer({}));
        // Zoom controls
        try {
            // @ts-expect-error — types only list deprecated "@yandex/ymaps3-controls@0.0.1"
            const controlsPkg = await this.api.import("@yandex/ymaps3-default-ui-theme");
            const controls = new this.api.YMapControls({ position: "right" });
            controls.addChild(new controlsPkg.YMapZoomControl({}));
            this.map.addChild(controls);
        }
        catch {
            // Non-critical — map works without zoom controls
            console.warn("[MapService] Failed to load zoom controls");
        }
        this.isInitialized = true;
    }
    // ─── Guards ──────────────────────────────────────────────────────
    ensureDestroyed() {
        if (this.map) {
            this.destroy();
        }
    }
    ensureMapReady() {
        if (!this.map || !this.isInitialized) {
            throw new Error("Map is not initialized");
        }
    }
    handleError(operation, error) {
        console.error(`${operation} failed:`, error);
        throw new Error(`${operation} failed`);
    }
    // ─── Locator (marker) management ─────────────────────────────────
    createLocatorElement(locator) {
        const wrapper = document.createElement("div");
        wrapper.style.cssText =
            "position:relative;cursor:pointer;display:flex;flex-direction:column;align-items:center;";
        // Pin circle
        const pin = document.createElement("div");
        pin.style.cssText = `
      background-color: ${MAP_COLORS.DEFAULT_LOCATOR};
      width: 25px; height: 25px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: bold; font-size: 12px;
    `;
        pin.textContent = locator.icon || "📍";
        wrapper.appendChild(pin);
        // Popup (hidden by default)
        if (locator.title || locator.description) {
            const popup = document.createElement("div");
            popup.style.cssText = `
        display: none; position: absolute; bottom: 35px; left: 50%;
        transform: translateX(-50%); background: white; color: #333;
        padding: 8px 12px; border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        white-space: nowrap; z-index: 10; font-size: 12px;
        pointer-events: auto; min-width: 120px;
      `;
            if (locator.title) {
                const h = document.createElement("strong");
                h.textContent = locator.title;
                h.style.display = "block";
                popup.appendChild(h);
            }
            if (locator.description) {
                const p = document.createElement("span");
                p.textContent = locator.description;
                p.style.cssText = "display:block;margin-top:2px;color:#666;";
                popup.appendChild(p);
            }
            if (locator.position.address) {
                const addr = document.createElement("small");
                addr.textContent = locator.position.address;
                addr.style.cssText = "display:block;margin-top:2px;color:#999;";
                popup.appendChild(addr);
            }
            wrapper.appendChild(popup);
            // Toggle popup on click
            pin.addEventListener("click", (e) => {
                e.stopPropagation();
                popup.style.display = popup.style.display === "none" ? "block" : "none";
            });
        }
        return wrapper;
    }
    async addLocator(locator) {
        try {
            this.ensureMapReady();
            const element = this.createLocatorElement(locator);
            const marker = new this.api.YMapMarker({
                coordinates: this.toLngLat(locator.position),
            }, element);
            this.map.addChild(marker);
            this.locators.set(locator.id, { marker, data: locator });
        }
        catch (error) {
            this.handleError("Add locator", error);
        }
    }
    async updateLocator(id, updates) {
        const entry = this.locators.get(id);
        if (!entry) {
            throw new Error(`Locator with id ${id} not found`);
        }
        const updated = { ...entry.data, ...updates };
        await this.removeLocator(id);
        await this.addLocator(updated);
    }
    async removeLocator(id) {
        const entry = this.locators.get(id);
        if (entry && this.map) {
            this.map.removeChild(entry.marker);
            this.locators.delete(id);
        }
    }
    async clearAllLocators() {
        if (!this.map)
            return;
        for (const id of Array.from(this.locators.keys())) {
            await this.removeLocator(id);
        }
    }
    getLocators() {
        return Array.from(this.locators.values()).map((e) => e.data);
    }
    getLocator(id) {
        return this.locators.get(id)?.data;
    }
    // ─── Route (polyline) management ────────────────────────────────
    isOriginalWaypoints(points) {
        return points.length < 50;
    }
    async getRoutePointsForDrawing(route) {
        if (route.points.length < 2)
            return route.points;
        // Skip road-building for actual GPS tracks
        if (route.skipRouteBuilding)
            return route.points;
        // Use pre-computed geometry from edge function
        if (route.precomputedGeometry && route.precomputedGeometry.length > 0) {
            return route.precomputedGeometry.map(([lat, lon], index) => ({
                latitude: lat,
                longitude: lon,
                address: `Route point ${index + 1}`,
                orderIndex: index,
            }));
        }
        // For routes without precomputed geometry: try to build via edge function
        if (this.isOriginalWaypoints(route.points)) {
            const built = await this.buildRoute(route.points);
            if (built && built.length > 0)
                return built;
        }
        return route.points;
    }
    createRoutePointElement(orderIndex, color) {
        const el = document.createElement("div");
        el.style.cssText = `
      background-color: ${color};
      width: 20px; height: 20px;
      border-radius: 50%;
      border: 2px solid white;
      display: flex; align-items: center; justify-content: center;
      color: white; font-weight: bold; font-size: 10px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    `;
        el.textContent = String(orderIndex + 1);
        return el;
    }
    async addRoute(route) {
        try {
            this.ensureMapReady();
            const drawPoints = await this.getRoutePointsForDrawing(route);
            // Create polyline via YMapFeature
            const coordinates = drawPoints.map((p) => this.toLngLat(p));
            const feature = new this.api.YMapFeature({
                geometry: {
                    type: "LineString",
                    coordinates,
                },
                style: {
                    stroke: [
                        {
                            width: route.weight || 4,
                            color: route.color || MAP_COLORS.PRIMARY_ROUTE,
                            opacity: 0.8,
                        },
                    ],
                },
            });
            this.map.addChild(feature);
            // Add waypoint markers for original route points
            const waypointMarkers = [];
            const waypoints = route.points.filter((p) => p.orderIndex !== undefined);
            for (const point of waypoints) {
                const el = this.createRoutePointElement(point.orderIndex, route.color || MAP_COLORS.PRIMARY_ROUTE);
                // Add popup on click
                const popup = document.createElement("div");
                popup.style.cssText = `
          display: none; position: absolute; bottom: 28px; left: 50%;
          transform: translateX(-50%); background: white; color: #333;
          padding: 6px 10px; border-radius: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          white-space: nowrap; z-index: 10; font-size: 11px;
        `;
                popup.innerHTML = `<strong>Точка ${point.orderIndex + 1}</strong><br/><span style="color:#666">${point.address}</span>`;
                const wrapper = document.createElement("div");
                wrapper.style.cssText =
                    "position:relative;cursor:pointer;display:flex;flex-direction:column;align-items:center;";
                wrapper.appendChild(el);
                wrapper.appendChild(popup);
                el.addEventListener("click", (e) => {
                    e.stopPropagation();
                    popup.style.display =
                        popup.style.display === "none" ? "block" : "none";
                });
                const marker = new this.api.YMapMarker({ coordinates: this.toLngLat(point) }, wrapper);
                this.map.addChild(marker);
                waypointMarkers.push(marker);
            }
            this.routes.set(route.id, { feature, waypointMarkers, data: route });
        }
        catch (error) {
            this.handleError("Add route", error);
        }
    }
    async updateRoute(id, updates) {
        const entry = this.routes.get(id);
        if (!entry) {
            throw new Error(`Route with id ${id} not found`);
        }
        const updated = { ...entry.data, ...updates };
        await this.removeRoute(id);
        await this.addRoute(updated);
    }
    async removeRoute(id) {
        const entry = this.routes.get(id);
        if (entry && this.map) {
            this.map.removeChild(entry.feature);
            for (const m of entry.waypointMarkers) {
                this.map.removeChild(m);
            }
            this.routes.delete(id);
        }
    }
    async clearAllRoutes() {
        if (!this.map)
            return;
        for (const id of Array.from(this.routes.keys())) {
            await this.removeRoute(id);
        }
    }
    getRoutes() {
        return Array.from(this.routes.values()).map((e) => e.data);
    }
    // ─── Map controls ────────────────────────────────────────────────
    setCenter(position) {
        this.map?.setLocation({
            center: this.toLngLat(position),
            duration: 300,
        });
    }
    setZoom(zoom) {
        this.map?.setLocation({ zoom, duration: 300 });
    }
    fitBounds(points) {
        if (!this.map || points.length === 0)
            return;
        if (points.length === 1) {
            // Combine center + zoom in a single setLocation call
            // to avoid the second call overriding the first animation
            this.map.setLocation({
                center: this.toLngLat(points[0]),
                zoom: 15,
                duration: 300,
            });
            return;
        }
        let minLng = Infinity, maxLng = -Infinity;
        let minLat = Infinity, maxLat = -Infinity;
        for (const p of points) {
            if (p.longitude < minLng)
                minLng = p.longitude;
            if (p.longitude > maxLng)
                maxLng = p.longitude;
            if (p.latitude < minLat)
                minLat = p.latitude;
            if (p.latitude > maxLat)
                maxLat = p.latitude;
        }
        // Add padding (~5% of extent)
        const lngPad = (maxLng - minLng) * 0.05 || 0.005;
        const latPad = (maxLat - minLat) * 0.05 || 0.005;
        this.map.setLocation({
            bounds: [
                [minLng - lngPad, minLat - latPad],
                [maxLng + lngPad, maxLat + latPad],
            ],
            duration: 300,
        });
    }
    // ymaps3 auto-tracks container size; no-op for compatibility
    resize() {
        // No-op: ymaps3 handles container resize automatically
    }
    // ─── Geocoding (not implemented) ─────────────────────────────────
    async geocodeAddress(_address) {
        return null;
    }
    async reverseGeocode(_position) {
        return null;
    }
    // ─── Route building ──────────────────────────────────────────────
    async buildRoute(points) {
        if (points.length < 2) {
            throw new Error("At least 2 points are required to build a route");
        }
        try {
            const geometry = await routeCalculationService.getRouteGeometry(points);
            if (geometry.length > 0) {
                return geometry.map(([lat, lon], index) => ({
                    latitude: lat,
                    longitude: lon,
                    address: `Route point ${index + 1}`,
                    orderIndex: index,
                }));
            }
        }
        catch {
            // fallback to straight lines silently
        }
        return points.map((point, index) => ({ ...point, orderIndex: index }));
    }
    // ─── Cleanup ─────────────────────────────────────────────────────
    destroy() {
        if (!this.map)
            return;
        // Clear data structures (markers/features are destroyed with the map)
        this.locators.clear();
        this.routes.clear();
        this.map.destroy();
        this.map = null;
        this.api = null;
        this.isInitialized = false;
        // ymaps3.destroy() may leave orphan <ymaps> elements in the container
        if (this.container) {
            const orphans = this.container.querySelectorAll("ymaps");
            orphans.forEach((el) => el.remove());
            this.container = null;
        }
    }
}
// Export singleton instance
export const mapService = new MapService();
